import { User } from '../services/adminService'
import { formatDate } from '../utils/dateUtils'
import { Table, TableColumn } from './Table'
import { useTranslation } from '../i18n/hooks/useTranslation'
import { Button } from './ui/Button'

interface UserListProps {
  users: User[]
  onEdit: (user: User) => void
}

export function UserList({ users, onEdit }: UserListProps) {
  const { t } = useTranslation()
  
  const getStatusBadge = (status: string) => {
    const styles = {
      active: 'bg-gray-100 text-gray-900 border border-gray-300',
      suspended: 'bg-gray-50 text-gray-600 border border-gray-200',
      deleted: 'bg-transparent text-gray-400 border border-gray-200',
    }
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-md ${styles[status as keyof typeof styles] || styles.active}`}>
        {status ? t(`common.${status}`) : t('common.unknown')}
      </span>
    )
  }

  const getRoleBadge = (role: string) => {
    const styles = {
      user: 'bg-transparent text-gray-700 border border-gray-300',
      moderator: 'bg-gray-100 text-gray-900 border border-gray-300',
      admin: 'bg-gray-900 text-white border border-gray-900',
    }
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-md ${styles[role as keyof typeof styles] || styles.user}`}>
        {role ? t(`users.roles.${role}`) : t('users.roles.user')}
      </span>
    )
  }

  // Role options for filter
  const roleOptions = [
    { value: 'user', label: t('users.roles.user') },
    { value: 'moderator', label: t('users.roles.moderator') },
    { value: 'admin', label: t('users.roles.admin') }
  ]

  // Status options for filter
  const statusOptions = [
    { value: 'active', label: t('common.active') },
    { value: 'suspended', label: t('common.suspended') },
    { value: 'deleted', label: t('common.deleted') }
  ]

  const columns: TableColumn<User>[] = [
    {
      key: 'user',
      header: t('users.title').slice(0, -1),
      sortKey: 'displayName',
      sortable: true,
      filterable: {
        type: 'text',
        placeholder: t('filters.filterBy.nameOrEmail'),
        filterKey: 'email' // We'll filter by email since it's more reliable
      },
      render: (user: User) => (
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10">
            {user.photoURL ? (
              <img
                className="h-10 w-10 rounded-full"
                src={user.photoURL}
                alt=""
              />
            ) : (
              <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                <span className="text-sm font-medium text-gray-700">
                  {user.displayName?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || '?'}
                </span>
              </div>
            )}
          </div>
          <div className="ml-4">
            <div className="text-sm text-gray-900">
              {user.displayName || t('users.noName')}
            </div>
            <div className="text-sm text-gray-500">
              {user.email}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'role',
      header: t('users.fields.role'),
      sortKey: 'role',
      sortable: true,
      filterable: {
        type: 'select',
        options: roleOptions,
        filterKey: 'role'
      },
      render: (user: User) => getRoleBadge(user.role)
    },
    {
      key: 'status',
      header: t('common.status'),
      sortKey: 'status',
      sortable: true,
      className: 'w-40',
      filterable: {
        type: 'select',
        options: statusOptions,
        filterKey: 'status'
      },
      render: (user: User) => getStatusBadge(user.status)
    },
    {
      key: 'lastLogin',
      header: t('users.fields.lastLogin'),
      sortKey: 'lastLoginAt',
      sortable: true,
      filterable: {
        type: 'date',
        filterKey: 'lastLoginAt'
      },
      render: (user: User) => (
        <span className="text-sm text-gray-500">
          {formatDate(user.lastLoginAt, undefined, t)}
        </span>
      )
    },
    {
      key: 'created',
      header: t('users.fields.created'),
      sortKey: 'createdAt',
      sortable: true,
      filterable: {
        type: 'date',
        filterKey: 'createdAt'
      },
      render: (user: User) => (
        <span className="text-sm text-gray-500">
          {formatDate(user.createdAt, undefined, t)}
        </span>
      )
    },
    {
      key: 'actions',
      header: t('common.actions'),
      className: 'text-right w-32',
      filterable: {
        type: 'none'
      },
      render: (user: User) => (
        <div className="flex">
          <Button
            onClick={() => onEdit(user)}
            variant="primary"
            size="sm"
          >
            {t('common.edit')}
          </Button>
        </div>
      )
    }
  ]

  return (
            <Table
      data={users}
      columns={columns}
      searchableFields={['displayName', 'email']}
      searchPlaceholder={t('users.searchPlaceholder')}
      emptyMessage="No users found"
      emptySubMessage="Users will appear here once they register"
      pageSize={15}
      t={t}
    />
  )
} 